import type { DateRange, WeekState } from "@/types";
import {
  getUnixTime,
  format,
  startOfHour,
  addHours,
  subYears,
  addYears,
  startOfDay,
  parseISO,
  startOfISOWeek,
  addDays,
} from "date-fns";
import { da } from "date-fns/locale";

export const convertToDate = (value: number | Date) => {
  return typeof value === "number" ? toDate(value) : value;
};

/**
 * Convert unix timestamp to Date object
 *
 * @param unix number
 * @returns Date
 * @example
 *  <script setup lang="ts">
 *    const date: Date = toDate(1697700327)
 *  </script>
 */
export const toDate = (unix: number) => {
  return new Date(unix * 1000);
};

/**
 * Convert Date object to unix timestamp
 *
 * @param unix number
 * @returns Date
 * @example
 *  <script setup lang="ts">
 *    const value: number = toUnix(new Date())
 *  </script>
 */
export const toUnix = (date: Date) => {
  return Math.round(getUnixTime(date));
};

/**
 * Convert Date object to ISO date string (YYYY-MM-DD format)
 *
 * @param date Date
 * @returns ISO date string
 * @example
 *  <script setup lang="ts">
 *    const isoDate: string = toISODate(new Date()) // "2026-03-18"
 *  </script>
 */
export const toISODate = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Parse ISO date string (YYYY-MM-DD format) to Date object
 *
 * @param isoDate ISO date string in YYYY-MM-DD format
 * @returns Date object
 * @example
 *  <script setup lang="ts">
 *    const date: Date = fromISODate("2026-03-18")
 *  </script>
 */
export const fromISODate = (isoDate: string): Date => {
  return parseISO(isoDate);
};

export const toDayName = (date: number) => {
  const name = format(toDate(date), "iiii", {
    locale: da,
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const toWeekNumber = (date: number) => {
  return format(toDate(date), "ww");
};

export const fromUnixToAPIDate = (date: number) => {
  return toAPIDate(toDate(date));
};

export const toAPIDate = (date: Date) => {
  return date.toISOString().split(".")[0] + "Z";
};

export const getStartOfToday = () => {
  return toUnix(startOfDay(new Date()));
};

export const getStartDate = () => {
  // It would return 14:00 if the time is 13:45
  const now = new Date();
  return toUnix(addHours(startOfHour(now), 1));
};

export const getCurrentISOWeek = (): WeekState => {
  const startOfWeek = startOfISOWeek(new Date());
  return {
    start: format(startOfWeek, "yyyy-MM-dd"),
    end: format(addDays(startOfWeek, 6), "yyyy-MM-dd"),
  };
};

export const getCurrentISODay = (): string => {
  return format(new Date(), "yyyy-MM-dd");
};

export const getEndDate = () => {
  // It would return 15:00 if the time is 13:45
  const now = new Date();
  return toUnix(addHours(startOfHour(now), 2));
};

export const toLongDateRange = (future: boolean): DateRange => {
  let dateFrom = subYears(new Date(), 100);
  let dateTo = new Date();

  if (future) {
    dateFrom = new Date();
    dateTo = addYears(new Date(), 100);
  }

  return { dateFrom: toAPIDate(dateFrom), dateTo: toAPIDate(dateTo) };
};

export const toExternalDateRange = (): DateRange => {
  const dateFrom = startOfDay(new Date());
  const dateTo = addYears(new Date(), 100);

  return { dateFrom: toAPIDate(dateFrom), dateTo: toAPIDate(dateTo) };
};

export const normalizeTime = (days: number, hours: number, minutes: number) => {
  // Convert minutes to hours
  hours += Math.floor(minutes / 60);
  minutes = minutes % 60;

  // Convert hours to days
  days += Math.floor(hours / 24);
  hours = hours % 24;

  return { days, hours, minutes };
};

export const minutesToTime = (totalMinutes: number) => {
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  return { days, hours, minutes };
};

export const hoursToTime = (totalHours: number) => {
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  return { days, hours, minutes: 0 };
};

/**
 * Format a Date to ISO string with timezone offset (e.g., 2026-04-13T11:00:00+00:00)
 * Backend expects format: Y-m-d\TH:i:sP
 */
export const formatDateTimeWithTimezone = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
};
